/**
 * Utility to extract base64-encoded images from SurveyJS JSON
 * and upload them to the MinIO file bucket, replacing inline data
 * with lightweight public URLs.
 */

const BASE64_DATA_URI_REGEX = /^data:(image\/[\w+]+);base64,(.+)$/;
const IMAGE_FIELD_KEYS = new Set(["imageLink", "logo", "backgroundImage"]);

interface ExtractResult {
  json: Record<string, unknown>;
  uploadCount: number;
  errors: string[];
}

interface Base64Field {
  obj: Record<string, unknown>;
  key: string;
  dataUri: string;
}

/**
 * Converts a base64 data URI to a File object and uploads it
 * to the file bucket as a public file.
 *
 * Returns the public URL: /api3.2/noauth/bucket/{id}
 */
export async function uploadBase64ToFileBucket(dataUri: string): Promise<string> {
  const match = dataUri.match(BASE64_DATA_URI_REGEX);
  if (!match) {
    throw new Error("Invalid base64 data URI");
  }

  const mimeType = match[1];
  const base64Data = match[2];

  // Decode base64 to binary
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // Derive file extension from MIME type
  const ext = mimeType.split("/")[1].replace("+xml", "") || "png";
  const filename = `survey-image-${Date.now()}.${ext}`;

  const blob = new Blob([bytes], { type: mimeType });
  const file = new File([blob], filename, { type: mimeType });

  const formData = new FormData();
  formData.append("file", file);
  formData.append("is_public", "true");

  const response = await fetch("/api3.2/bucket/upload", {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Upload failed: ${response.status}`);
  }

  const data = await response.json();
  return `/api3.2/noauth/bucket/${data.id}`;
}

/**
 * Recursively walks a JSON object and collects all fields
 * that contain base64 image data URIs.
 */
function collectBase64Fields(
  node: unknown,
  results: Base64Field[]
): void {
  if (Array.isArray(node)) {
    for (const item of node) {
      collectBase64Fields(item, results);
    }
  } else if (node !== null && typeof node === "object") {
    const obj = node as Record<string, unknown>;
    for (const key of Object.keys(obj)) {
      const value = obj[key];
      if (
        IMAGE_FIELD_KEYS.has(key) &&
        typeof value === "string" &&
        value.startsWith("data:image/")
      ) {
        results.push({ obj, key, dataUri: value });
      }
      collectBase64Fields(value, results);
    }
  }
}

/**
 * Extracts all base64 images from a SurveyJS JSON object,
 * uploads them to the file bucket in parallel, and replaces
 * the data URIs with public URLs.
 *
 * Returns a modified clone of the JSON — the original is not mutated.
 * Failed uploads are left as base64 (graceful degradation).
 */
export async function extractAndUploadSurveyImages(
  surveyJson: Record<string, unknown>
): Promise<ExtractResult> {
  const cloned = structuredClone(surveyJson);
  const fields: Base64Field[] = [];
  collectBase64Fields(cloned, fields);

  if (fields.length === 0) {
    return { json: cloned, uploadCount: 0, errors: [] };
  }

  // Dedup: avoid uploading the same base64 string multiple times
  const uploadCache = new Map<string, Promise<string>>();

  const uploadPromises = fields.map(async (field) => {
    let uploadPromise = uploadCache.get(field.dataUri);
    if (!uploadPromise) {
      uploadPromise = uploadBase64ToFileBucket(field.dataUri);
      uploadCache.set(field.dataUri, uploadPromise);
    }
    return { field, urlPromise: uploadPromise };
  });

  const settled = await Promise.allSettled(
    uploadPromises.map(async (p) => {
      const { field, urlPromise } = await p;
      const url = await urlPromise;
      field.obj[field.key] = url;
      return url;
    })
  );

  let uploadCount = 0;
  const errors: string[] = [];

  for (const result of settled) {
    if (result.status === "fulfilled") {
      uploadCount++;
    } else {
      errors.push(result.reason?.message || "Unknown upload error");
    }
  }

  return { json: cloned, uploadCount, errors };
}
